// Core types for the application

export interface User {
  id: string;
  userid: string;
  firstname: string;
  lastname: string;
  username: string;
  mobilenumber: string;
  email: string;
  role: string;
  isactive: string;
  createdat?: string;
  updatedat?: string;
  cretedby?: string;
  modifiedby?: string;
  // Legacy fields for backward compatibility
  name?: string;
  staffId?: string;
  phone?: string;
  roles?: UserRole[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  password?: string;
  packedTransactions?: number;
  receivedTransactions?: number;
  lastTransaction?: string;
}

export enum UserRole {
  ADMIN = 'admin',
  PACKER = 'packer',
  RECEIVER = 'receiver',
  SECURITY = 'security'
}

export interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface Vanchi {
  id: string;
  sNo: number;
  sealDateTime: string;
  transNo: string;
  nada: string;
  vanchi: string;
  vanchiNo: string;
  bagNo: string;
  sealedBy: string;
  securityGuard: string;
  receivedDateTime?: string;
  receivedBy?: string;
  status: VanchiStatus;
  comments?: string;
}

export enum VanchiStatus {
  SEALED = 'sealed',
  IN_TRANSIT = 'in_transit',
  RECEIVED = 'received',
  VERIFIED = 'verified',
  REJECTED = 'rejected'
}

export interface VanchiStats {
  today: { packedSent: number; received: number };
  yesterday: { packedSent: number; received: number };
  week: { packedSent: number; received: number };
  month: { packedSent: number; received: number };
  year: { packedSent: number; received: number };
}

export interface VanchiState {
  list: any[];
  total: number;
  stats: VanchiStats;
  loading: boolean;
  error: string | null;
}

export interface MasterItem {
  id: string;
  name: string;
  code?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Nada extends MasterItem {
  createdBy: string;
}

export interface CreateNadaRequest {
  name: string;
}

export interface VanchiName extends MasterItem {
  nadaId: string;
  nadaName: string;
  vanchiName: string;
  createdBy: string;
}

export interface CreateVanchiNameRequest {
  nadaId: string;
  vanchiName: string;
}

export interface VanchiNameCreatePayload {
  nadacd: string;
  nadaname: string;
  vanchicd: string;
  vanchiname: string;
  isactive: string;
  cretedby: string;
}

export interface VanchiNo extends MasterItem {
  nadaId: string;
  nadaName: string;
  vanchiNameId: string;
  vanchiName: string;
  vanchiNumber: string;
  createdBy: string;
}

export interface CreateVanchiNoRequest {
  nadaId: string;
  vanchiNameId: string;
  vanchiNumber: string;
}

export interface MastersState {
  nada: Nada[];
  vanchiNames: VanchiName[];
  total: number;
  loading: boolean;
  error: string | null;
}

// UsersState is now defined in the Redux slice to use ApiUser[]

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: any, rowIndex?: number, pagination?: SimplePaginationInfo) => React.ReactNode;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  // Edit column functionality
  isEditColumn?: boolean;
  onEditClick?: (row: any) => void;
  editIcon?: React.ReactNode;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SimplePaginationInfo {
  page: number;
  limit: number;
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  roles?: UserRole[];
}

export interface FormFieldProps {
  label: string;
  type: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  rules?: any;
  options?: { value: string; label: string }[];
}

export interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface LoginCredentials {
  userid: string;
  password: string;
}

export interface CreateUserRequest {
  userid: string;
  password: string;
  firstname: string;
  lastname: string;
  username: string;
  mobilenumber: string;
  email: string;
  role: string;
  isactive: string;
  cretedby: string; // Required - will be populated by slice from auth state
  // Legacy fields for backward compatibility
  name?: string;
  staffId?: string;
  confirmPassword?: string;
  phone?: string;
  roles?: UserRole[];
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

export interface AppState {
  auth: AuthState;
  vanchi: VanchiState;
  users: any;
  masters: MastersState;
}
